import * as React from 'react';
import axios from 'axios';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-pro';
import { alpha, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { useAuth } from 'context/auth-provider';
import { useAmaV2Context } from 'context/ama-v2-provider';
import DataGridPagination from 'components/data-grid-pagination';
import { getUtcDatetimeDisplayString } from 'utils/util';
import { getEventDetails, getEvents } from './events.api';
import { isEventSortField } from './events.types';
import type {
  EventDetails,
  EventFilterValues,
  EventSummary,
} from './events.types';
import EventDataDrawer from './event-data-drawer';
import EventFiltersDrawer from './event-filters-drawer';
import type { ClientFilterOption } from './event-filters-drawer';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 400;

const EMPTY_EVENT_FILTERS: EventFilterValues = {
  eventName: '',
  clientName: '',
  operationStatus: '',
  initiatedBy: '',
};

const EVENT_FILTER_CONFIG: ReadonlyArray<{
  key: keyof EventFilterValues;
  label: string;
}> = [
  { key: 'eventName', label: 'Event' },
  { key: 'clientName', label: 'Client' },
  { key: 'operationStatus', label: 'Status' },
  { key: 'initiatedBy', label: 'Initiated By' },
];

const areEventFiltersEqual = (left: EventFilterValues, right: EventFilterValues): boolean =>
  EVENT_FILTER_CONFIG.every(({ key }) => left[key] === right[key]);

const activeFilterChipStyles = (theme: Theme) => ({
  bgcolor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.dark,
  '& .MuiChip-deleteIcon': {
    color: alpha(theme.palette.primary.main, 0.7),
    '&:hover': {
      color: theme.palette.primary.dark,
    },
  },
});

export const parseEventSearch = (input: string): string[] => {
  const normalizedSearch = input.trim();
  return normalizedSearch.length >= MIN_SEARCH_LENGTH ? [normalizedSearch] : [];
};

interface EventsToolbarProps {
  activeFilterCount: number;
  onOpenFilters: () => void;
}

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    activeFilterCount: EventsToolbarProps['activeFilterCount'];
    onOpenFilters: EventsToolbarProps['onOpenFilters'];
  }
}

const EventsToolbar = ({ activeFilterCount, onOpenFilters }: EventsToolbarProps) => (
  <GridToolbarContainer sx={{ justifyContent: 'flex-end', px: 1, py: 0.5 }}>
    <Button
      size="small"
      startIcon={(
        <Badge badgeContent={activeFilterCount} color="primary" invisible={activeFilterCount === 0}>
          <FilterListIcon />
        </Badge>
      )}
      onClick={onOpenFilters}
    >
      Apply Filters
    </Button>
    <GridToolbarQuickFilter
      debounceMs={SEARCH_DEBOUNCE_MS}
      quickFilterParser={parseEventSearch}
      placeholder="Search events (3+ characters)"
      inputProps={{ 'aria-label': 'Search events' }}
    />
  </GridToolbarContainer>
);

const displayValue = (value?: string | null): string => value || '--';

const getErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (axios.isAxiosError(error)) {
    const responseMessage = error.response?.data?.message;
    return typeof responseMessage === 'string' && responseMessage
      ? responseMessage
      : error.message;
  }

  return error instanceof Error ? error.message : fallbackMessage;
};

export default function EventsView() {
  const theme = useTheme();
  const { user } = useAuth();
  const { clientsState, loadClients } = useAmaV2Context();
  const [events, setEvents] = React.useState<EventSummary[]>([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = React.useState<EventSummary | null>(null);
  const [eventDetails, setEventDetails] = React.useState<EventDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = React.useState(false);
  const [detailsError, setDetailsError] = React.useState<string | null>(null);
  const [detailsRefreshSequence, setDetailsRefreshSequence] = React.useState(0);
  const [refreshSequence, setRefreshSequence] = React.useState(0);
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [appliedFilters, setAppliedFilters] = React.useState<EventFilterValues>(
    () => ({ ...EMPTY_EVENT_FILTERS }),
  );
  const [draftFilters, setDraftFilters] = React.useState<EventFilterValues>(
    () => ({ ...EMPTY_EVENT_FILTERS }),
  );
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'created_at', sort: 'desc' },
  ]);
  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const latestRequest = React.useRef(0);
  const latestDetailsRequest = React.useRef(0);
  const idTokenRef = React.useRef(user?.idToken);
  const searchQuery = filterModel.quickFilterValues?.[0]?.trim() || '';
  const selectedEventId = selectedEvent?.event_id;
  const { clientName, eventName, initiatedBy, operationStatus } = appliedFilters;
  const clientOptions = React.useMemo<ClientFilterOption[]>(() => {
    const options = new Map<string, ClientFilterOption>();

    clientsState.data.forEach((client) => {
      const value = client.display_name?.trim() || client.client_id.trim();
      if (value) {
        options.set(value, { label: value, value });
      }
    });

    return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [clientsState.data]);
  const hasActiveFilters = Boolean(
    searchQuery || eventName || clientName || operationStatus || initiatedBy,
  );
  const activeFilterChips = EVENT_FILTER_CONFIG.filter(
    ({ key }) => Boolean(appliedFilters[key]),
  );

  React.useEffect(() => {
    idTokenRef.current = user?.idToken;
  }, [user?.idToken]);

  React.useEffect(() => {
    if (clientsState.status === 'idle') {
      void loadClients();
    }
  }, [clientsState.status, loadClients]);

  const loadEvents = React.useCallback(async (signal: AbortSignal): Promise<void> => {
    const idToken = idTokenRef.current;
    if (!idToken) return;

    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;

    setLoading(true);
    setError(null);

    const requestedSort = sortModel[0];
    const sortField = requestedSort && isEventSortField(requestedSort.field)
      ? requestedSort.field
      : undefined;

    try {
      const response = await getEvents({
        clientName: clientName || undefined,
        eventName: eventName || undefined,
        idToken,
        initiatedBy: initiatedBy || undefined,
        operationStatus: operationStatus || undefined,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: searchQuery || undefined,
        signal,
        sortField,
        sortDirection: sortField ? requestedSort?.sort || undefined : undefined,
      });

      if (signal.aborted || latestRequest.current !== requestId) return;

      setEvents(Array.isArray(response.items) ? response.items : []);
      setRowCount(Number.isFinite(response.totalItems) ? response.totalItems : 0);
    } catch (requestError: unknown) {
      if (
        signal.aborted ||
        axios.isCancel(requestError) ||
        latestRequest.current !== requestId
      ) {
        return;
      }

      setError(getErrorMessage(requestError, 'Failed to load events'));
    } finally {
      if (!signal.aborted && latestRequest.current === requestId) {
        setLoading(false);
      }
    }
  }, [
    clientName,
    eventName,
    initiatedBy,
    operationStatus,
    paginationModel.page,
    paginationModel.pageSize,
    searchQuery,
    sortModel,
  ]);

  const loadEventDetails = React.useCallback(async (
    eventId: string,
    signal: AbortSignal,
  ): Promise<void> => {
    const idToken = idTokenRef.current;
    if (!idToken) return;

    const requestId = latestDetailsRequest.current + 1;
    latestDetailsRequest.current = requestId;

    setDetailsLoading(true);
    setDetailsError(null);

    try {
      const details = await getEventDetails({
        eventId,
        idToken,
        signal,
      });

      if (signal.aborted || latestDetailsRequest.current !== requestId) return;

      setEventDetails(details);
    } catch (requestError: unknown) {
      if (
        signal.aborted ||
        axios.isCancel(requestError) ||
        latestDetailsRequest.current !== requestId
      ) {
        return;
      }

      setDetailsError(getErrorMessage(requestError, 'Failed to load event details'));
    } finally {
      if (!signal.aborted && latestDetailsRequest.current === requestId) {
        setDetailsLoading(false);
      }
    }
  }, []);

  React.useEffect(() => {
    const abortController = new AbortController();
    void loadEvents(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [loadEvents, refreshSequence]);

  React.useEffect(() => {
    if (!selectedEventId) return;

    const abortController = new AbortController();
    void loadEventDetails(selectedEventId, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [detailsRefreshSequence, loadEventDetails, selectedEventId]);

  const handlePaginationModelChange = React.useCallback((nextModel: GridPaginationModel) => {
    setPaginationModel((currentModel) => ({
      page: nextModel.pageSize === currentModel.pageSize ? nextModel.page : 0,
      pageSize: nextModel.pageSize,
    }));
  }, []);

  const handleSortModelChange = React.useCallback((nextModel: GridSortModel) => {
    setSortModel(nextModel.slice(0, 1));
    setPaginationModel((currentModel) =>
      currentModel.page === 0 ? currentModel : { ...currentModel, page: 0 });
  }, []);

  const handleFilterModelChange = React.useCallback((nextModel: GridFilterModel) => {
    setFilterModel(nextModel);
    setPaginationModel((currentModel) =>
      currentModel.page === 0 ? currentModel : { ...currentModel, page: 0 });
  }, []);

  const resetPage = React.useCallback(() => {
    setPaginationModel((currentModel) =>
      currentModel.page === 0 ? currentModel : { ...currentModel, page: 0 });
  }, []);

  const handleClearDraftFilters = React.useCallback(() => {
    setDraftFilters({ ...EMPTY_EVENT_FILTERS });
  }, []);

  const handleRemoveAppliedFilter = React.useCallback((key: keyof EventFilterValues) => {
    setAppliedFilters((currentFilters) => ({ ...currentFilters, [key]: '' }));
    setDraftFilters((currentFilters) => ({ ...currentFilters, [key]: '' }));
    resetPage();
  }, [resetPage]);

  const handleApplyFilters = React.useCallback(() => {
    const nextFilters = {
      ...draftFilters,
      initiatedBy: draftFilters.initiatedBy.trim(),
    };
    setAppliedFilters((currentFilters) =>
      areEventFiltersEqual(currentFilters, nextFilters) ? currentFilters : nextFilters);
    setDraftFilters(nextFilters);
    resetPage();
    setFiltersOpen(false);
  }, [draftFilters, resetPage]);

  const handleCancelFilters = React.useCallback(() => {
    setDraftFilters(appliedFilters);
    setFiltersOpen(false);
  }, [appliedFilters]);

  const handleOpenFilters = React.useCallback(() => {
    if (clientsState.status === 'error') {
      void loadClients();
    }

    setFiltersOpen(true);
  }, [clientsState.status, loadClients]);

  const handleEventSelection = React.useCallback((event: EventSummary) => {
    setSelectedEvent(event);
    setEventDetails(null);
    setDetailsError(null);
    setDetailsLoading(true);
    setDetailsRefreshSequence((sequence) => sequence + 1);
  }, []);

  const handleDetailsClose = React.useCallback(() => {
    setSelectedEvent(null);
    setEventDetails(null);
    setDetailsError(null);
    setDetailsLoading(false);
  }, []);

  const columns = React.useMemo<Array<GridColDef<EventSummary>>>(() => [
    {
      field: 'event_name',
      headerName: 'Event Name',
      minWidth: 180,
      flex: 1,
      sortable: true,
      pinnable: true,
      filterable: false,
    },
    {
      field: 'client_name',
      headerName: 'Client Name',
      minWidth: 180,
      flex: 1,
      sortable: true,
      pinnable: true,
      filterable: false,
      renderCell: (params: GridRenderCellParams<EventSummary>) =>
        displayValue(params.row.client_name),
    },
    {
      field: 'operation_status',
      headerName: 'Operation Status',
      minWidth: 140,
      flex: 0.75,
      sortable: true,
      pinnable: true,
      filterable: false,
    },
    {
      field: 'initiated_by',
      headerName: 'Initiated By',
      minWidth: 180,
      flex: 0.9,
      sortable: true,
      pinnable: true,
      filterable: false,
      renderCell: (params: GridRenderCellParams<EventSummary>) =>
        displayValue(params.row.initiated_by),
    },
    {
      field: 'created_at',
      headerName: 'Created At (UTC)',
      minWidth: 210,
      flex: 0.9,
      sortable: true,
      pinnable: true,
      filterable: false,
      renderCell: (params: GridRenderCellParams<EventSummary>) =>
        getUtcDatetimeDisplayString(params.row.created_at),
    },
  ], []);

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography component="h2" variant="h6" color="primary" sx={{ flexGrow: 1 }}>
            Events
          </Typography>
          <Tooltip title="Refresh events">
            <span>
              <IconButton
                aria-label="Refresh events"
                disabled={loading}
                onClick={() => setRefreshSequence((sequence) => sequence + 1)}
                sx={{ color: theme.palette.primary.main }}
              >
                <CachedIcon
                  sx={{
                    animation: loading ? 'spin 1s linear infinite' : 'none',
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={(
              <IconButton
                aria-label="Retry loading events"
                color="inherit"
                size="small"
                onClick={() => setRefreshSequence((sequence) => sequence + 1)}
              >
                <CachedIcon fontSize="inherit" />
              </IconButton>
            )}
          >
            {error}
          </Alert>
        )}

        {activeFilterChips.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              Active Filters:
            </Typography>
            {activeFilterChips.map(({ key, label }) => (
              <Chip
                key={key}
                label={`${label}: "${appliedFilters[key]}"`}
                onDelete={() => handleRemoveAppliedFilter(key)}
                size="small"
                sx={activeFilterChipStyles}
              />
            ))}
          </Stack>
        )}

        <Box sx={{ minHeight: 300, flexGrow: 1 }}>
          <DataGridPro
            rows={events}
            columns={columns}
            getRowId={(row) => row.event_id}
            onRowClick={(params) => handleEventSelection(params.row)}
            onCellKeyDown={(params, event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleEventSelection(params.row);
              }
            }}
            loading={loading}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            filterMode="server"
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            rowCount={rowCount}
            localeText={{
              noRowsLabel: hasActiveFilters
                ? 'No matching events found for the selected filter criteria'
                : 'No events found',
            }}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            disableMultipleColumnsSorting
            disableRowSelectionOnClick
            slots={{
              toolbar: EventsToolbar,
              pagination: DataGridPagination,
            }}
            slotProps={{
              toolbar: {
                activeFilterCount: activeFilterChips.length,
                onOpenFilters: handleOpenFilters,
              },
            }}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'
            }
            sx={{
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
              },
              '& .MuiDataGrid-row.Mui-even .MuiDataGrid-cell': {
                backgroundColor: theme.palette.grey[100],
              },
              '& .MuiDataGrid-row:hover .MuiDataGrid-cell': {
                backgroundColor: theme.palette.grey[300],
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
            }}
          />
        </Box>
      </Paper>

      <EventDataDrawer
        details={eventDetails}
        error={detailsError}
        event={selectedEvent}
        loading={detailsLoading}
        onClose={handleDetailsClose}
        onRetry={() => setDetailsRefreshSequence((sequence) => sequence + 1)}
      />

      <EventFiltersDrawer
        clientOptions={clientOptions}
        clientsLoading={clientsState.status === 'loading'}
        filters={draftFilters}
        onApply={handleApplyFilters}
        onCancel={handleCancelFilters}
        onClear={handleClearDraftFilters}
        onFiltersChange={setDraftFilters}
        open={filtersOpen}
      />
    </Box>
  );
}
